import React, { useState } from 'react'
import { Bot, MessageCircle, ShieldCheck, Zap, LayoutDashboard, TrendingUp } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import Header from '../components/Header'
import Footer from '../components/Footer'
import PageMeta from '../components/PageMeta'
import './ServicesPage.css'

const fmt = (n) => 'R ' + n.toLocaleString('en-ZA')

const ServicesPage = () => {
  const [activeSection, setActiveSection] = useState('website')
  const [pkgTab, setPkgTab] = useState('business')

  /* -- Quote Builder state -- */
  const [qPkg, setQPkg] = useState(null)
  const [qAddons, setQAddons] = useState({})
  const [qMonthly, setQMonthly] = useState({})

  const toggleAddon = (id) => setQAddons(p => ({ ...p, [id]: !p[id] }))
  const toggleMonthly = (id) => setQMonthly(p => ({ ...p, [id]: !p[id] }))

  /* -- Data -- */
  const packages = [
    {
      id: 'starter', name: 'Starter', price: 8500, display: 'R 8,500', period: 'once-off',
      desc: 'Solo traders, new businesses needing a credible online presence fast.',
      features: ['Up to 5 pages', 'WordPress + custom theme', 'Mobile-first responsive', 'WhatsApp CTA button', 'Basic on-page SEO', 'Contact form + Google Maps', 'SSL certificate'],
    },
    {
      id: 'business', name: 'Business', price: 18500, display: 'R 18,500', period: 'once-off', popular: true, badge: 'Most popular',
      desc: 'SMEs needing a professional lead-generating site with content management.',
      features: ['Up to 12 pages', 'Custom CMS (WordPress)', 'Blog / news module', 'Advanced SEO setup', 'Google Analytics 4 + Search Console', 'Speed optimisation', 'Basic AI Q&A chatbot', 'POPIA compliance banner'],
    },
    {
      id: 'ecommerce', name: 'E-Commerce', price: 32000, display: 'R 32,000', period: 'once-off',
      desc: 'Retailers needing a full WooCommerce store with local payment gateways.',
      features: ['Unlimited products', 'PayFast / Yoco integration', 'Inventory management', 'Order tracking emails', 'Product SEO + schema', 'Custom CMS for stock', 'AI Q&A chatbot (product queries)'],
    },
    {
      id: 'corporate', name: 'Corporate / React', price: 55000, display: 'R 55,000+', period: 'POQ',
      desc: 'Established businesses needing a React-powered platform or complex integrations.',
      features: ['React / Next.js development', 'Custom CMS (Headless)', 'API integrations (CRM, ERP)', 'Advanced AI chatbot', 'Custom Digital Marketing CMS', 'Performance SLA', 'Dedicated project manager'],
    },
  ]

  const hostingPlans = [
    { plan: 'Basic Hosting', includes: 'cPanel hosting, SSL, weekly backups, uptime monitoring', rate: 'R 350/mo' },
    { plan: 'Care Plan — Starter', includes: 'Hosting + security updates + 1 hr monthly support', rate: 'R 850/mo' },
    { plan: 'Care Plan — Growth', includes: 'Above + plugin updates, content edits, monthly report', rate: 'R 1,800/mo' },
    { plan: 'Care Plan — Pro', includes: 'Above + private cloud hosting, priority support, CRO review', rate: 'R 3,500/mo' },
  ]

  const aiBots = [
    {
      id: 'basic', name: 'Basic FAQ Bot', badge: 'New', price: 'R 5,500', period: 'setup',
      monthly: '+ R 450/mo licence & maintenance',
      desc: 'Rule-based bot answering up to 30 preset FAQs. Ideal for Starter/Business sites.',
      features: ['Pre-built Q&A flows', 'WhatsApp handoff', 'Email lead capture', 'Mobile-optimised widget'],
    },
    {
      id: 'knowledge', name: 'AI Knowledge Bot', badge: 'Recommended', price: 'R 12,500', period: 'setup', popular: true,
      monthly: '+ R 950/mo licence & maintenance',
      desc: 'Trained on your website content. Answers naturally, qualifies leads, escalates to WhatsApp.',
      features: ['AI trained on your content', 'Natural language understanding', 'Lead scoring + CRM push', 'WhatsApp / email handoff', 'Conversation analytics', 'Monthly retraining'],
    },
    {
      id: 'enterprise', name: 'Full AI Agent', badge: 'Enterprise', price: 'R 28,000+', period: 'POQ',
      monthly: '+ R 2,200/mo licence & maintenance',
      desc: 'Multi-channel AI agent on website, WhatsApp & email. Custom workflows and integrations.',
      features: ['Omnichannel (web, WhatsApp, email)', 'Custom API integrations', 'Booking / quote automation', 'South African language support', 'Dedicated AI manager'],
    },
  ]

  const cmsPricing = [
    { type: 'WordPress CMS Setup Core', desc: 'Custom post types, ACF fields, user roles, media library — full client training included', price: 'R 4,500', best: 'Most SMEs' },
    { type: 'Multi-User Editorial CMS', desc: 'Role-based access, editorial workflow, content approval pipeline', price: 'R 9,500', best: 'News / blogs / schools' },
    { type: 'Headless CMS (Sanity / Contentful)', desc: 'API-first CMS for React/Next.js frontends. Structured content with preview', price: 'R 14,000+', best: 'React & corporate builds' },
    { type: 'CMS Training Session', desc: '2-hour live walkthrough for client team (Zoom or on-site Centurion)', price: 'R 1,500', best: 'All clients' },
  ]

  const dmCmsModules = [
    { module: 'Blog & Content Hub', features: 'SEO-scored posts, categories, author profiles, schema markup', price: 'R 6,500 setup' },
    { module: 'Landing Page Builder', features: 'Drag-and-drop campaign pages with A/B testing hooks', price: 'R 8,500 setup' },
    { module: 'Social Content Planner', features: 'Schedule captions + visuals across platforms from one dashboard', price: 'R 7,500 setup' },
    { module: 'Campaign Performance Dashboard', features: 'Live stats: GA4, Google Ads, Meta Ads — all in one view', price: 'R 11,000 setup' },
    { module: 'Full Digital Marketing CMS Suite', features: 'All above modules bundled — 15% discount applied', price: 'R 28,500 setup' },
    { module: 'Monthly CMS Retainer', features: 'Updates, new landing pages, content uploads, plugin maintenance', price: 'R 2,200/mo' },
  ]

  const otherAddons = [
    { addon: 'WhatsApp Business API integration', price: 'R 3,500 setup + R 550/mo' },
    { addon: 'Booking / appointment system', price: 'R 4,500 setup' },
    { addon: 'Job board / careers portal', price: 'R 6,500 setup' },
    { addon: 'Client portal (login + documents)', price: 'R 9,500 setup' },
    { addon: 'POPIA compliance audit + policy pages', price: 'R 2,500 once-off' },
    { addon: 'Speed optimisation (existing site)', price: 'R 3,500 once-off' },
    { addon: 'Additional language (e.g. Afrikaans)', price: 'R 3,500 per language' },
  ]

  const retainers = [
    {
      name: 'Visibility', price: 'R 4,500', period: '/month',
      desc: 'Local SEO focus for businesses in Centurion, Pretoria, Midrand area.',
      features: ['Local SEO (Google Business Profile)', 'On-page optimisation (4 pages)', 'Monthly ranking report', 'Citation building'],
    },
    {
      name: 'Growth', price: 'R 9,500', period: '/month', popular: true, badge: 'Best value',
      desc: 'SEO + Google Ads + social media for businesses serious about digital growth.',
      features: ['Full SEO (technical + content)', 'Google Ads management (excl. adspend)', '2 social media platforms', '8 posts/month', 'Monthly performance report', 'GA4 + Search Console setup'],
    },
    {
      name: 'Authority', price: 'R 18,500', period: '/month',
      desc: 'Full-funnel digital marketing for brands wanting to dominate their niche in SA.',
      features: ['Advanced SEO + link building', 'Google + Meta Ads management', '4 social platforms', '16 posts/month + reels', 'Email marketing (monthly campaign)', 'Digital Marketing CMS managed', 'Bi-weekly strategy calls'],
    },
  ]

  const individualServices = [
    { service: 'SEO audit (technical + content gap)', rate: 'R 4,500 once-off' },
    { service: 'Google Ads management (excl. adspend)', rate: 'R 3,500/mo or 15% of adspend' },
    { service: 'Meta Ads management (excl. adspend)', rate: 'R 3,000/mo or 15% of adspend' },
    { service: 'Social media management (2 platforms)', rate: 'From R 3,500/mo' },
    { service: 'Content writing (blog posts)', rate: 'R 1,200 per post (800–1,200 words, SEO optimised)' },
    { service: 'Email marketing (design + send)', rate: 'R 2,500 per campaign' },
    { service: 'Hourly consulting / strategy', rate: 'R 850/hr' },
  ]

  const automationTiers = [
    {
      id: 'auto_starter',
      name: 'Starter Workflows',
      badge: null,
      price: 'R 4,500',
      period: 'setup',
      monthly: '+ R 650/mo hosting & maintenance',
      desc: 'Small businesses needing simple automations: form-to-CRM, email notifications, and basic n8n flows.',
      features: [
        'n8n instance setup & configuration',
        'Up to 3 workflow automations',
        'Form submissions → email / Slack',
        'Daily / weekly report emails',
        'WhatsApp notification triggers',
        'Basic CRM push (HubSpot / Zoho)',
      ],
    },
    {
      id: 'auto_business',
      name: 'Business Automation',
      badge: 'Recommended',
      price: 'R 9,500',
      period: 'setup',
      popular: true,
      monthly: '+ R 1,200/mo hosting & maintenance',
      desc: 'SMEs wanting to automate lead nurturing, social media scheduling, and multi-step workflows across tools.',
      features: [
        'Everything in Starter +',
        'Up to 8 workflow automations',
        'AI-generated social media posts (FB / IG)',
        'Telegram approval flow for content',
        'Monthly analytics digest email',
        'CRM sync & lead scoring',
        'WhatsApp Business API integration',
      ],
    },
    {
      id: 'auto_enterprise',
      name: 'Full Automation Stack',
      badge: 'Enterprise',
      price: 'R 22,000+',
      period: 'POQ',
      monthly: '+ R 2,500/mo retainer',
      desc: 'Agencies and growth-stage businesses needing a full automation ecosystem with AI, multi-channel sequences, and custom integrations.',
      features: [
        'Everything in Business +',
        'Unlimited workflow automations',
        'Multi-channel AI content (posts, emails, ads)',
        'Custom API integrations (ERP, CRM, POS)',
        'WhatsApp broadcast sequences',
        'Automated reporting dashboards',
        'Dedicated automation manager',
      ],
    },
  ]

  const automationServices = [
    { service: 'n8n instance setup & managed hosting', rate: 'R 2,500 setup + R 650/mo' },
    { service: 'Social media auto-posting (AI-generated content)', rate: 'R 5,500 setup + R 850/mo' },
    { service: 'Email drip sequence (up to 5 emails)', rate: 'R 2,500 once-off' },
    { service: 'WhatsApp broadcast automation', rate: 'R 3,500 setup + R 550/mo' },
    { service: 'CRM integration (HubSpot / Zoho / custom)', rate: 'R 4,500 once-off' },
    { service: 'Lead capture & scoring workflow', rate: 'R 3,500 once-off' },
    { service: 'Automation audit & optimisation (existing setup)', rate: 'R 2,500 once-off' },
  ]

  const growthDrivers = [
    { Icon: Bot,              title: 'AI-Powered Websites',    desc: 'SA businesses expect AI chatbots, automated lead capture, and intelligent content. AI add-ons are now a differentiator, not a luxury.' },
    { Icon: MessageCircle,    title: 'WhatsApp-First UX',       desc: 'South Africa has 90%+ WhatsApp penetration. Every website needs tap-to-WhatsApp CTAs and bots that hand off to WhatsApp naturally.' },
    { Icon: ShieldCheck,      title: 'POPIA Compliance',         desc: 'Clients increasingly ask about POPIA. Compliance audits, cookie consent banners, and privacy policy generation add real value and trust.' },
    { Icon: Zap,              title: 'Performance-First Builds', desc: "Google's Core Web Vitals affect rankings directly. Guaranteed PageSpeed scores above 90 is a strong competitive differentiator." },
    { Icon: LayoutDashboard,  title: 'Digital Marketing CMS',    desc: 'SMEs want to manage their own content without calling the agency. A CMS that manages landing pages, social captions, and ad copy is a recurring-revenue opportunity.' },
    { Icon: TrendingUp,       title: 'Recurring Revenue Model',  desc: 'A client on a R 1,800/mo Care Plan is worth R 21,600/year. Bundle hosting + maintenance + SEO basics for stable, predictable income.' },
  ]

  const updateComparison = [
    { current: 'Custom WordPress websites from R 8,500', recommended: 'Tier the offering: Starter / Business / E-Commerce / Corporate. Bundle AI chatbot into Business tier.', impact: '+25–40% per deal' },
    { current: 'WordPress development', recommended: 'Add Headless CMS / React tier for enterprise clients at R 55k+. Position as premium.', impact: 'New revenue segment' },
    { current: 'Local SEO from R 4,500/mo', recommended: 'Repackage as "Visibility" plan. Create clear Growth and Authority tiers above it.', impact: 'Easier upsells' },
    { current: 'Google Ads management', recommended: 'Bundle with Meta Ads into a "Paid Media" module. Minimum R 3,500/mo management fee.', impact: 'Stable retainer income' },
    { current: 'Private cloud hosting', recommended: 'Bundle into Care Plan Pro. Position as a premium differentiator over shared hosting.', impact: 'Higher retention' },
    { current: 'No AI chatbot offering', recommended: 'Launch AI Q&A Bot as a new product line. 3 tiers from R 5,500 setup + monthly SaaS fee.', impact: 'New product revenue' },
    { current: 'No Digital Marketing CMS', recommended: 'Build and offer Digital Marketing CMS Suite at R 28,500 setup + R 2,200/mo.', impact: 'High-value anchor product' },
  ]

  /* -- Quote Builder options -- */
  const qPkgOptions = [
    { id: 'starter', label: 'Starter Website', price: 8500 },
    { id: 'business', label: 'Business Website', price: 18500 },
    { id: 'ecommerce', label: 'E-Commerce Website', price: 32000 },
    { id: 'corporate', label: 'Corporate / React (from)', price: 55000 },
  ]
  const qAddonOptions = [
    { id: 'bot_basic', label: 'AI FAQ Bot', price: 5500 },
    { id: 'bot_knowledge', label: 'AI Knowledge Bot', price: 12500 },
    { id: 'bot_enterprise', label: 'Full AI Agent', price: 28000 },
    { id: 'whatsapp', label: 'WhatsApp Business API', price: 3500 },
    { id: 'booking', label: 'Booking / Appointment System', price: 4500 },
    { id: 'job_board', label: 'Job Board / Careers Portal', price: 6500 },
    { id: 'client_portal', label: 'Client Portal', price: 9500 },
    { id: 'popia', label: 'POPIA Compliance Audit', price: 2500 },
    { id: 'speed', label: 'Speed Optimisation (existing site)', price: 3500 },
    { id: 'dm_blog', label: 'DM CMS — Blog & Content Hub', price: 6500 },
    { id: 'dm_landing', label: 'DM CMS — Landing Page Builder', price: 8500 },
    { id: 'dm_social', label: 'DM CMS — Social Content Planner', price: 7500 },
    { id: 'dm_full', label: 'DM CMS — Full Suite (save 15%)', price: 28500 },
    { id: 'auto_starter', label: 'Automation — Starter Workflows', price: 4500 },
    { id: 'auto_business', label: 'Automation — Business', price: 9500 },
    { id: 'auto_social', label: 'Social Media Auto-Posting (AI)', price: 5500 },
  ]
  const qMonthlyOptions = [
    { id: 'host_basic', label: 'Basic Hosting', price: 350 },
    { id: 'care_starter', label: 'Care Plan — Starter', price: 850 },
    { id: 'care_growth', label: 'Care Plan — Growth', price: 1800 },
    { id: 'care_pro', label: 'Care Plan — Pro', price: 3500 },
    { id: 'seo_vis', label: 'SEO — Visibility Plan', price: 4500 },
    { id: 'seo_growth', label: 'SEO — Growth Plan', price: 9500 },
    { id: 'seo_auth', label: 'SEO — Authority Plan', price: 18500 },
    { id: 'bot_maint', label: 'AI Knowledge Bot Maintenance', price: 950 },
    { id: 'dm_retainer', label: 'DM CMS Monthly Retainer', price: 2200 },
    { id: 'wa_monthly', label: 'WhatsApp API Monthly', price: 550 },
    { id: 'auto_maint', label: 'Automation Maintenance (n8n hosting)', price: 1200 },
    { id: 'auto_social_maint', label: 'Social Media Auto-Posting Maintenance', price: 850 },
  ]

  const quoteOnce =
    (qPkgOptions.find(p => p.id === qPkg)?.price || 0) +
    qAddonOptions.filter(a => qAddons[a.id]).reduce((s, a) => s + a.price, 0)
  const quoteMonthly =
    qMonthlyOptions.filter(m => qMonthly[m.id]).reduce((s, m) => s + m.price, 0)

  const quoteLineItems = [
    ...qPkgOptions.filter(p => p.id === qPkg),
    ...qAddonOptions.filter(a => qAddons[a.id]),
  ]
  const monthlyLineItems = qMonthlyOptions.filter(m => qMonthly[m.id])

  /* -- Current package -- */
  const activePkg = packages.find(p => p.id === pkgTab) || packages[1]

  const sectionNav = [
    { id: 'website', label: 'Website Packages' },
    { id: 'addons', label: 'Add-ons & AI' },
    { id: 'digital', label: 'Digital Marketing' },
    { id: 'automation', label: 'Automations' },
    { id: 'quote', label: 'Quote Builder' },
  ]

  const socialMediaPlans = [
    {
      id: 'sm_starter',
      name: 'Starter',
      badge: null,
      price: 'R 1,500',
      period: '/month',
      posts: '3 posts per week',
      desc: 'Perfect for businesses just getting started with consistent social media presence.',
      features: [
        '3 custom posts per week',
        'Facebook & Instagram',
        'Custom-written captions',
        'On-brand visuals',
        'Scheduled & published for you',
      ],
    },
    {
      id: 'sm_growth',
      name: 'Growth',
      badge: 'Most Popular',
      price: 'R 2,500',
      period: '/month',
      popular: true,
      posts: '5 posts per week',
      desc: 'Ideal for businesses ready to grow their audience and stay consistently visible.',
      features: [
        '5 custom posts per week',
        'Facebook, Instagram & Threads',
        'Custom-written captions',
        'On-brand visuals',
        'Scheduled & published for you',
        'Monthly performance summary',
      ],
    },
    {
      id: 'sm_pro',
      name: 'Pro',
      badge: 'Max Visibility',
      price: 'R 4,500',
      period: '/month',
      posts: '3 posts per day',
      desc: 'For brands that want to dominate their feed and stay top-of-mind every single day.',
      features: [
        '3 custom posts per day',
        'All major platforms',
        'Custom-written captions',
        'On-brand visuals',
        'Fully managed scheduling',
        'Monthly performance report',
        'Content calendar provided',
      ],
    },
  ]

  return (
    <div>
      <PageMeta
        title="Services & Pricing 2026 | Web Design, AI Bots, SEO Centurion | RDS Studio"
        description="RDS Studio 2026 pricing guide. Website packages from R 8,500, AI chatbots from R 5,500, SEO retainers from R 4,500/mo. Centurion, Gauteng — POPIA-compliant & mobile-first."
        canonical="https://rdsstudio.co.za/services"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "url": "https://rdsstudio.co.za/services",
            "name": "Services & Pricing 2026 | RDS Studio",
            "description": "Web design packages, AI chatbots, CMS, digital marketing retainers — benchmarked against the South African market.",
            "inLanguage": "en-ZA"
          })}
        </script>
      </Helmet>
      <Header />
      <main className="main-content">

        {/* -- Hero -- */}
        <section className="section" style={{ paddingTop: '120px', paddingBottom: '2rem', minHeight: 'unset', alignItems: 'flex-start' }}>
          <div className="section-inner">
            <h1 className="title title-h2">Services &amp; Pricing</h1>
            <p className="description" style={{ marginBottom: '0.5rem' }}>
              Centurion, Gauteng — Benchmarked against the South African market
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>
              All prices excl. VAT (15%) — Five clear tiers aligned to 2026 SA market demand.
              Mobile-first, POPIA-compliant and SEO-optimised from day one.
            </p>
          </div>
        </section>

        {/* -- Sticky category nav -- */}
        <nav className="sp-sticky-nav">
          <div className="sp-nav__inner">
            {sectionNav.map(s => (
              <button
                key={s.id}
                className={`sp-nav__btn${activeSection === s.id ? ' active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* -- Section content -- */}
        <div className="section sp-content">
          <div className="section-inner">

            {/* ----------------------------------------
                WEBSITE PACKAGES
            ---------------------------------------- */}
            {activeSection === 'website' && (
              <div>
                <h2 className="sp-section-title">Website Packages</h2>
                <p className="sp-section-sub">
                  Five clear tiers aligned to 2026 SA market demand. Every build includes a free discovery call before any quote is issued.
                </p>

                {/* Package tier tabs */}
                <div className="sp-pkg-tabs">
                  {packages.map(pkg => (
                    <button
                      key={pkg.id}
                      className={`sp-pkg-tab${pkgTab === pkg.id ? ' active' : ''}`}
                      onClick={() => setPkgTab(pkg.id)}
                    >
                      {pkg.name}
                      {pkg.popular && <span className="sp-tab-badge">Popular</span>}
                    </button>
                  ))}
                </div>

                {/* Active package card */}
                <div className={`sp-pkg-card${activePkg.popular ? ' sp-pkg-card--popular' : ''}`}>
                  {/* Left: pricing + description */}
                  <div>
                    {activePkg.badge && <div className="sp-pkg-badge">{activePkg.badge}</div>}
                    <div className="sp-pkg-price">{activePkg.display}</div>
                    <div className="sp-pkg-period">{activePkg.period}</div>
                    <div className="sp-pkg-name">{activePkg.name}</div>
                    <p className="sp-pkg-desc">{activePkg.desc}</p>
                    <a href="/contact" className="btn btn-primary">Get a Quote</a>
                  </div>
                  {/* Right: features */}
                  <div>
                    <ul className="sp-features">
                      {activePkg.features.map((f, i) => (
                        <li key={i}><span className="chk">✓</span>{f}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="sp-note">
                  <strong>Pricing note:</strong> All prices are starting rates. Final quotes depend on number of pages, custom functionality, third-party integrations, and content requirements. A free discovery call is included before any quote is issued.
                </div>

                {/* -- Maintenance & Hosting -- */}
                <div className="sp-hosting-wrap">
                  <h3 className="sp-sub-title">Website Maintenance &amp; Hosting</h3>
                  <p className="sp-sub-desc">Keep your site fast, secure and up-to-date with one of our Care Plans.</p>
                  <div className="sp-table-wrap">
                    <table className="sp-table">
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Includes</th>
                          <th>Monthly Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hostingPlans.map((h, i) => (
                          <tr key={i}>
                            <td className="td-name">{h.plan}</td>
                            <td>{h.includes}</td>
                            <td className="td-price">{h.rate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ----------------------------------------
                ADD-ONS & AI
            ---------------------------------------- */}
            {activeSection === 'addons' && (
              <div>
                <h2 className="sp-section-title">Add-ons &amp; AI</h2>
                <p className="sp-section-sub">
                  Modular add-ons for any website package. These are the modern services SA businesses need in 2026.
                </p>

                {/* AI Q&A Bots */}
                <h3 className="sp-sub-title">AI Q&amp;A Bot — powered by Claude / GPT</h3>
                <p className="sp-sub-desc">Add an AI chatbot to any website package.</p>
                <div className="sp-tiers">
                  {aiBots.map(bot => (
                    <div key={bot.id} className={`sp-tier${bot.popular ? ' sp-tier--popular' : ''}`}>
                      {bot.badge && <div className="sp-tier__badge">{bot.badge}</div>}
                      <div className="sp-tier__name">{bot.name}</div>
                      <div className="sp-tier__price">{bot.price}</div>
                      <div className="sp-tier__period">{bot.period}</div>
                      <div className="sp-tier__monthly">{bot.monthly}</div>
                      <p className="sp-tier__desc">{bot.desc}</p>
                      <ul className="sp-features">
                        {bot.features.map((f, i) => (
                          <li key={i}><span className="chk">✓</span>{f}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <hr className="sp-divider" />

                {/* Custom CMS */}
                <h3 className="sp-sub-title">Custom CMS</h3>
                <p className="sp-sub-desc">The right CMS for your stack and team.</p>
                <div className="sp-table-wrap">
                  <table className="sp-table">
                    <thead>
                      <tr>
                        <th>CMS Type</th>
                        <th>Description</th>
                        <th>Price</th>
                        <th>Best For</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cmsPricing.map((c, i) => (
                        <tr key={i}>
                          <td className="td-name">{c.type}</td>
                          <td>{c.desc}</td>
                          <td className="td-price">{c.price}</td>
                          <td><span className="td-badge">{c.best}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <hr className="sp-divider" />

                {/* Digital Marketing CMS */}
                <h3 className="sp-sub-title">Digital Marketing CMS</h3>
                <div className="sp-what-is">
                  <div className="sp-what-is__label">What is a Digital Marketing CMS?</div>
                  <p className="sp-what-is__text">
                    A purpose-built content hub that lets your team manage blog posts, landing pages, campaign pages, social media captions, Google Ads copy, and email templates — all from one place, with SEO scoring built in. Built on WordPress or a custom React dashboard depending on your stack.
                  </p>
                </div>
                <div className="sp-table-wrap">
                  <table className="sp-table">
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Features</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dmCmsModules.map((m, i) => (
                        <tr key={i}>
                          <td className="td-name">{m.module}</td>
                          <td>{m.features}</td>
                          <td className="td-price">{m.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <hr className="sp-divider" />

                {/* Other add-ons */}
                <h3 className="sp-sub-title">Other Popular Add-ons</h3>
                <div className="sp-table-wrap">
                  <table className="sp-table">
                    <thead>
                      <tr>
                        <th>Add-on</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherAddons.map((a, i) => (
                        <tr key={i}>
                          <td className="td-name">{a.addon}</td>
                          <td className="td-price">{a.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ----------------------------------------
                AUTOMATIONS
            ---------------------------------------- */}
            {activeSection === 'automation' && (
              <div>
                <h2 className="sp-section-title">Marketing Automation</h2>
                <p className="sp-section-sub">
                  Self-hosted n8n workflow automation — AI-powered social media posting, lead nurturing, CRM integration and multi-step business workflows. No Zapier subscription fees, no vendor lock-in, your data stays on your server.
                </p>

                {/* Benchmarks */}
                <div className="sp-benchmarks">
                  <div className="sp-benchmark">
                    <div className="sp-benchmark__label">Zapier Business (market rate)</div>
                    <div className="sp-benchmark__value">$49–$69/mo USD</div>
                    <div className="sp-benchmark__rds">RDS self-hosted n8n from R 650/mo</div>
                  </div>
                  <div className="sp-benchmark">
                    <div className="sp-benchmark__label">Social media management avg</div>
                    <div className="sp-benchmark__value">R 3,500+/mo</div>
                    <div className="sp-benchmark__rds">AI auto-posting from R 850/mo</div>
                  </div>
                  <div className="sp-benchmark">
                    <div className="sp-benchmark__label">Full marketing automation setup</div>
                    <div className="sp-benchmark__value">R 15–30k+</div>
                    <div className="sp-benchmark__rds">RDS Business Automation R 9,500</div>
                  </div>
                </div>

                {/* Tier cards */}
                <div className="sp-tiers">
                  {automationTiers.map(tier => (
                    <div key={tier.id} className={`sp-tier${tier.popular ? ' sp-tier--popular' : ''}`}>
                      {tier.badge && <div className="sp-tier__badge">{tier.badge}</div>}
                      <div className="sp-tier__name">{tier.name}</div>
                      <div className="sp-tier__price">{tier.price}</div>
                      <div className="sp-tier__period">{tier.period}</div>
                      <div className="sp-tier__monthly">{tier.monthly}</div>
                      <p className="sp-tier__desc">{tier.desc}</p>
                      <ul className="sp-features">
                        {tier.features.map((f, i) => (
                          <li key={i}><span className="chk">✓</span>{f}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <hr className="sp-divider" />

                {/* Individual services */}
                <h3 className="sp-sub-title">Individual Automation Services</h3>
                <div className="sp-table-wrap">
                  <table className="sp-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {automationServices.map((s, i) => (
                        <tr key={i}>
                          <td className="td-name">{s.service}</td>
                          <td className="td-price">{s.rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="sp-note">
                  <strong>Why n8n over Zapier?</strong> n8n is open-source and self-hosted on your own VPS — no per-task pricing, no subscription price hikes, and full data ownership. RDS Studio handles deployment, maintenance and workflow building. You keep the server and the workflows.
                </div>

                <hr className="sp-divider" />

                {/* ── Social Media Autopilot ── */}
                <h3 className="sp-sub-title">Social Media Autopilot</h3>
                <p className="sp-sub-desc">
                  Done-for-you social media management. We write the content, design the visuals, schedule and publish — completely hands-off for you, every week.
                </p>

                <div className="sp-tiers">
                  {socialMediaPlans.map(plan => (
                    <div key={plan.id} className={`sp-tier${plan.popular ? ' sp-tier--popular' : ''}`}>
                      {plan.badge && <div className="sp-tier__badge">{plan.badge}</div>}
                      <div className="sp-tier__name">{plan.name}</div>
                      <div className="sp-tier__price">{plan.price}</div>
                      <div className="sp-tier__period">{plan.period}</div>
                      <div className="sp-tier__monthly" style={{ color: 'rgba(255,255,255,0.55)' }}>{plan.posts}</div>
                      <p className="sp-tier__desc">{plan.desc}</p>
                      <ul className="sp-features">
                        {plan.features.map((f, i) => (
                          <li key={i}><span className="chk">✓</span>{f}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Reels add-on */}
                <div className="sp-reel-addon">
                  <div className="sp-reel-addon__icon">🎬</div>
                  <div className="sp-reel-addon__body">
                    <div className="sp-reel-addon__title">Video Reels — Add-on</div>
                    <div className="sp-reel-addon__desc">Short-form vertical video reels for Instagram Reels, Facebook Reels & TikTok. Custom-edited, branded, and published for you. Available as a standalone or added to any Social Media Autopilot plan.</div>
                  </div>
                  <div className="sp-reel-addon__price">R 450 <span>/ reel</span></div>
                </div>

                <div className="sp-note" style={{ marginTop: '1.5rem' }}>
                  <strong>All plans include:</strong> Custom-written captions tailored to your brand voice, on-brand visuals, scheduling and publishing. No recycled templates, no generic content. Platforms covered per plan. Contact us for multi-platform pricing.
                </div>
              </div>
            )}

            {/* ----------------------------------------
                DIGITAL MARKETING
            ---------------------------------------- */}
            {activeSection === 'digital' && (
              <div>
                <h2 className="sp-section-title">Digital Marketing</h2>
                <p className="sp-section-sub">
                  Ongoing digital marketing retainers. All packages include monthly reporting and a dedicated account manager. All ad spend budgets are billed directly by Google/Meta to your card.
                </p>

                {/* Market benchmarks */}
                <div className="sp-benchmarks">
                  <div className="sp-benchmark">
                    <div className="sp-benchmark__label">SA SEO retainer (market avg)</div>
                    <div className="sp-benchmark__value">R 3—20k/mo</div>
                    <div className="sp-benchmark__rds">RDS from R 4,500/mo</div>
                  </div>
                  <div className="sp-benchmark">
                    <div className="sp-benchmark__label">SA social media management avg</div>
                    <div className="sp-benchmark__value">R 3.5k+/mo</div>
                    <div className="sp-benchmark__rds">Included in Growth+</div>
                  </div>
                  <div className="sp-benchmark">
                    <div className="sp-benchmark__label">Full digital marketing avg</div>
                    <div className="sp-benchmark__value">R 10—29k/mo</div>
                    <div className="sp-benchmark__rds">RDS Authority R 18,500/mo</div>
                  </div>
                </div>

                {/* Retainer tiers */}
                <div className="sp-tiers">
                  {retainers.map(r => (
                    <div key={r.name} className={`sp-tier${r.popular ? ' sp-tier--popular' : ''}`}>
                      {r.badge && <div className="sp-tier__badge">{r.badge}</div>}
                      <div className="sp-tier__name">{r.name}</div>
                      <div className="sp-tier__price">{r.price}</div>
                      <div className="sp-tier__period">{r.period}</div>
                      <p className="sp-tier__desc">{r.desc}</p>
                      <ul className="sp-features">
                        {r.features.map((f, i) => (
                          <li key={i}><span className="chk">✓</span>{f}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <hr className="sp-divider" />

                {/* Individual services */}
                <h3 className="sp-sub-title">Individual Services (standalone)</h3>
                <div className="sp-table-wrap">
                  <table className="sp-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {individualServices.map((s, i) => (
                        <tr key={i}>
                          <td className="td-name">{s.service}</td>
                          <td className="td-price">{s.rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="sp-note">
                  <strong>Ad spend note:</strong> Monthly ad spend budgets are charged directly by Google/Meta to the client's card and are separate from RDS Studio management fees. Recommended minimum ad spend: Google Ads R 3,000/mo — Meta Ads R 2,000/mo for meaningful results in the Gauteng market.
                </div>
              </div>
            )}

            {/* ----------------------------------------
                QUOTE BUILDER
            ---------------------------------------- */}
            {activeSection === 'quote' && (
              <div>
                <h2 className="sp-section-title">Quote Builder</h2>
                <p className="sp-section-sub">
                  Select your base package, add-ons and monthly services to get an instant estimate. All prices excl. VAT. A free discovery call is included before any final quote.
                </p>

                <div className="sp-quote-grid">
                  {/* Left: selectors */}
                  <div>
                    {/* Step 1: Package */}
                    <div className="sp-quote-panel" style={{ marginBottom: '1.25rem' }}>
                      <h3>Step 1 — Choose a Website Package</h3>
                      {qPkgOptions.map(p => (
                        <label key={p.id} className="sp-check-row">
                          <input
                            type="radio"
                            name="pkg"
                            checked={qPkg === p.id}
                            onChange={() => setQPkg(p.id)}
                          />
                          <span style={{ flex: 1, color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', cursor: 'pointer' }}>{p.label}</span>
                          <span className="cr-price">{fmt(p.price)}</span>
                        </label>
                      ))}
                    </div>

                    {/* Step 2: Add-ons */}
                    <div className="sp-quote-panel" style={{ marginBottom: '1.25rem' }}>
                      <h3>Step 2 — Add-ons (once-off)</h3>
                      {qAddonOptions.map(a => (
                        <label key={a.id} className="sp-check-row">
                          <input
                            type="checkbox"
                            checked={!!qAddons[a.id]}
                            onChange={() => toggleAddon(a.id)}
                          />
                          <span style={{ flex: 1, color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', cursor: 'pointer' }}>{a.label}</span>
                          <span className="cr-price">{fmt(a.price)}</span>
                        </label>
                      ))}
                    </div>

                    {/* Step 3: Monthly */}
                    <div className="sp-quote-panel">
                      <h3>Step 3 — Monthly Services</h3>
                      {qMonthlyOptions.map(m => (
                        <label key={m.id} className="sp-check-row">
                          <input
                            type="checkbox"
                            checked={!!qMonthly[m.id]}
                            onChange={() => toggleMonthly(m.id)}
                          />
                          <span style={{ flex: 1, color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', cursor: 'pointer' }}>{m.label}</span>
                          <span className="cr-price">{fmt(m.price)}/mo</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Right: summary */}
                  <div className="sp-quote-sticky">
                    <div className="sp-quote-summary">
                      <div className="sp-quote-summary__lbl">Estimated once-off investment</div>
                      <div className="sp-quote-summary__amount">{fmt(quoteOnce)}</div>
                      {quoteMonthly > 0 && (
                        <div className="sp-quote-summary__monthly">
                          + {fmt(quoteMonthly)}/mo ongoing
                        </div>
                      )}
                      <div className="sp-quote-summary__note">
                        Starting estimate excl. VAT. Final quote after discovery call.
                      </div>
                    </div>

                    {/* Line items */}
                    {quoteLineItems.length > 0 && (
                      <div className="sp-quote-items">
                        {quoteLineItems.map(item => (
                          <div key={item.id} className="sp-quote-item">
                            <span>{item.label}</span>
                            <span className="qi-price">{fmt(item.price)}</span>
                          </div>
                        ))}
                        <div className="sp-quote-item" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '0.5rem', paddingTop: '0.5rem', fontWeight: 700, color: '#fff' }}>
                          <span>Total once-off</span>
                          <span className="qi-price" style={{ color: '#2DD4BF' }}>{fmt(quoteOnce)}</span>
                        </div>
                      </div>
                    )}
                    {monthlyLineItems.length > 0 && (
                      <div className="sp-quote-items">
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.35)', marginBottom: '0.75rem' }}>Monthly services</div>
                        {monthlyLineItems.map(item => (
                          <div key={item.id} className="sp-quote-item">
                            <span>{item.label}</span>
                            <span className="qi-price">{fmt(item.price)}/mo</span>
                          </div>
                        ))}
                        <div className="sp-quote-item" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '0.5rem', paddingTop: '0.5rem', fontWeight: 700, color: '#fff' }}>
                          <span>Total monthly</span>
                          <span className="qi-price" style={{ color: '#2DD4BF' }}>{fmt(quoteMonthly)}/mo</span>
                        </div>
                      </div>
                    )}
                    {quoteLineItems.length === 0 && monthlyLineItems.length === 0 && (
                      <div className="sp-quote-items">
                        <div className="sp-quote-empty">Select items above to build your estimate</div>
                      </div>
                    )}

                    <a
                      href={`/contact?ref=quote`}
                      className="btn btn-primary"
                      style={{ width: '100%', textAlign: 'center', display: 'block', marginTop: '1rem' }}
                    >
                      Request This Quote
                    </a>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: '0.75rem' }}>
                      A free 30-min discovery call is included before any final quote is issued.
                    </p>
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default ServicesPage
